import { useRef, useState, useMemo, useEffect } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { offerings } from "../data/business";

// item 45: tabs by category — All / Whitewater / Lodging / Dining / Outdoors / Groups
const tabs = [
  { id: "all",        label: "All",        match: () => true },
  { id: "whitewater", label: "Whitewater", match: (l: string) => l === "Whitewater" },
  { id: "lodging",    label: "Lodging",    match: (l: string) => l === "Lodging" },
  { id: "dining",     label: "Dining",     match: (l: string) => l === "Dining" },
  { id: "outdoors",   label: "Outdoors",   match: (l: string) => l === "Outdoors" || l === "Free" },
  { id: "groups",     label: "Groups",     match: (l: string) => l === "Groups" },
];

// item 49: which months the river runs (TVA Ocoee release season)
const inSeason = () => {
  const m = new Date().getMonth() + 1; // 1..12
  return m >= 3 && m <= 11;
};

export function Offerings() {
  const root = useRef<HTMLElement>(null);
  const [tab, setTab] = useState("all");

  const filtered = useMemo(() => {
    const t = tabs.find((x) => x.id === tab) ?? tabs[0];
    return offerings.filter((o) => t.match(o.label as string));
  }, [tab]);

  // #182 IntersectionObserver fade-up on each card, staggered by index.
  // Gated by prefers-reduced-motion so we don't move anything for users who
  // opt out. Cheap to run — one observer, disconnects after first paint.
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const cards = root.current?.querySelectorAll<HTMLElement>(".cards--polished .card");
    if (!cards || cards.length === 0) return;
    cards.forEach((c, i) => {
      c.style.setProperty("--card-stagger", `${i * 60}ms`);
      c.classList.add("card--reveal");
    });
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-revealed");
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [filtered.length]);

  useGSAP(
    () => {
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (reduce) return;

      gsap.from(".offering-head > *", {
        y: 16,
        opacity: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 85%" },
      });
    },
    { scope: root },
  );

  const seasonOpen = inSeason();

  return (
    <section
      ref={root}
      id="offerings"
      className="section section-vertical section-center section--dark"
    >
      <div className="offering-head" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <span className="eye">OUR OFFERINGS</span>
        <h2 className="h2" style={{ textAlign: "center" }}>
          Pick your adventure
        </h2>
        <p className="body" style={{ maxWidth: 580, textAlign: "center" }}>
          From river trips to on-site lodging, dining, and nearby waterfall
          hikes in the Cherokee National Forest — you'll find what you need for
          an Ocoee getaway on or near the property.
        </p>

        {/* item 49: rafting season status */}
        <span className={`season-pill ${seasonOpen ? "is-open" : "is-closed"}`}>
          <span className="dot" />
          {seasonOpen
            ? "Rafting season — typically spring through early fall, following the Ocoee dam-release schedule"
            : "Off-season — lodging & dining open year-round"}
        </span>
      </div>

      {/* item 45: category tabs */}
      <div className="offerings-tabs" role="tablist" aria-label="Filter offerings by category">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? "is-active" : ""}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* item 46: lodge vs bunkhouse comparison helper */}
      {tab === "lodging" && (
        <p className="body" style={{ maxWidth: 720, textAlign: "center", color: "var(--accent-cream)" }}>
          <strong style={{ color: "var(--accent-gold)" }}>Which one?</strong> The
          Lodge is private rooms with bathrooms, microwave, fridge — couples and
          families. The Bunkhouse is shared bunks with bring-your-own bedding —
          groups, scouts, and budget travelers. Camping is for tent / RV.
        </p>
      )}

      <div className="cards cards--polished">
        {filtered.map((o) => {
          // #173: card title id pairs with the link's aria-labelledby so the
          // whole tile reads as one focusable named item to screen readers.
          const titleId = `offering-${o.slug}-title`;
          // #177: on-site badge only when business.ts `meta` flags it.
          const onSite = /on-site/i.test(o.meta);
          return (
            <Link
              key={o.slug}
              to={o.link}
              className={`card offering-card ${o.slug === "rafting" ? "is-featured" : ""}`}
              aria-labelledby={titleId}
            >
              <div
                className="card-img"
                style={{ backgroundImage: `url('/${o.img}')` }}
                role="img"
                aria-label={o.title}
              />
              <div className="card-body">
                <span className="label">{o.label}</span>
                <h3 className="card-title" id={titleId}>{o.title}</h3>
                <p className="card-desc">{o.desc}</p>
                <div className="card-meta-row">
                  <span className="card-meta">{o.meta}</span>
                  {onSite && <span className="card-badge card-badge--onsite">On-site</span>}
                </div>
                {/* New round #38: consistent price-from line on every card */}
                {"priceFrom" in o && (o as { priceFrom?: string }).priceFrom && (
                  <span className="card-price">{(o as { priceFrom?: string }).priceFrom}</span>
                )}
                <span className="card-link">{o.cta} <ArrowRight size={14} aria-hidden="true" /></span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
